import { useState } from "react"
import { useWalletActions } from "./actions"
import { formSchema } from "./withdrawal.schema"
import { z } from "zod"
import { toast } from "sonner"
import WithdrawalForm from "./withdrawal.form"
import Loader from "@/components/layout/loader"
import { useWithdrawalsActions } from "@/api/withdrawals"
import SendAmountForm from "@/transactions/send/send-amount.form"
import { formSchema as amountFormSchema } from "@/transactions/send/send-amount.schema"
import { Network, Token } from "@/config/tokens"

interface WithdrawalFlowProps {
  onStart: () => void;
  onDone: () => void;
}

export default function WithdrawalFlow({
  onDone,
  onStart,
}: WithdrawalFlowProps) {
  const [step, setStep] = useState<"amount" | "bank" | "process">("amount")
  const { makePayment } = useWalletActions()
  const { create, update } = useWithdrawalsActions()
  const [message, setMessage] = useState<string>("")
  const [amount, setAmount] = useState<string>()
  const [network, setNetwork] = useState<Network>()
  const [token, setToken] = useState<Token>(Token.USDC)

  const onAmountSubmit = ({
    amount,
    network,
    token,
  }: z.infer<typeof amountFormSchema>) => {
    setAmount(amount)
    setNetwork(network)
    setToken(token)
    setStep("bank")
  }

  const onSubmit = async ({ bankAccountId }: z.infer<typeof formSchema>) => {
    if (!amount || !network || !token) throw new Error("Invalid state")
    onStart()
    setStep("process")
    setMessage("Requesting withdrawal...")
    const withdrawal = await create({
      token,
      network,
      amount: Number(amount),
      bankAccountId,
    })
    setMessage("Transferring funds...")
    const { transactionId } = await makePayment(
      token,
      network,
      Number(amount),
      withdrawal.address,
      () => setMessage("Waiting for confirmation..."),
    )
    await update(withdrawal.id, transactionId)
    toast.success("Withdrawal request submitted")
    setStep("amount")
    onDone()
  }

  if (step === "amount") {
    return (
      <SendAmountForm
        onSubmit={onAmountSubmit}
        networks={Object.values(Network)}
        amount={amount}
      />
    )
  } else if (step === "bank") {
    return (
      <WithdrawalForm
        onSubmit={onSubmit}
        onBack={() => setStep("amount")}
        amount={Number(amount)}
        token={token}
      />
    )
  } else if (step === "process") {
    return <Loader message={message} />
  }
}
